'use client';

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { authCallbackUrl } from '@/lib/siteUrl';
import { getClient, clearStaleAuth, isStaleSessionError } from '@/lib/supabase/client';
import type { User, Session } from '@supabase/supabase-js';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signInWithGoogle: (returnTo?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({} as AuthContextValue);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Track whether bootstrap has completed so the onAuthStateChange initial
  // fire does not duplicate the bootstrap work or trigger extra auth requests.
  const bootstrappedRef = useRef(false);

  useEffect(() => {
    // Use the singleton client — never recreate it
    const supabase = getClient();

    // If the Supabase client is not available, bail out safely
    if (!supabase?.auth) {
      setLoading(false);
      return;
    }

    // Single bootstrap call: getSession() only.
    // Wrapped in try/catch so stale/corrupted tokens never surface as runtime errors.
    const bootstrap = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();

        if (error) {
          if (isStaleSessionError(error)) {
            clearStaleAuth();
          }
          setSession(null);
          setUser(null);
        } else {
          setSession(data.session);
          setUser(data.session?.user ?? null);
        }
      } catch (err: unknown) {
        if (isStaleSessionError(err)) {
          clearStaleAuth();
        }
        setSession(null);
        setUser(null);
      } finally {
        bootstrappedRef.current = true;
        setLoading(false);
      }
    };

    bootstrap();

    // Subscribe to auth state changes (login, logout, token refresh).
    // We skip the very first event fired synchronously by onAuthStateChange
    // because bootstrap() already handles the initial session — this prevents
    // a duplicate auth request on page load.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      // Skip the initial synchronous event; bootstrap handles it.
      if (!bootstrappedRef.current) return;

      try {
        // Only publish a genuinely different session.
        //
        // Supabase re-emits on every tab focus and on every silent token
        // refresh, handing over a new object each time. Storing it
        // unconditionally gave `user` a new identity on every one of those,
        // and anything downstream keyed on `user` — which is both remaining
        // providers — treated that as "the user changed" and refetched. On a
        // page with a form open, the refetch flipped a loading flag and the
        // form was unmounted mid-typing.
        //
        // Compared by access token rather than by object: a real refresh
        // issues a new token and must propagate, while a re-announcement of
        // the same session must not.
        setSession((prev) => {
          if (prev?.access_token === newSession?.access_token) return prev;
          return newSession;
        });
        setUser((prev) => {
          const next = newSession?.user ?? null;
          if (prev?.id === next?.id && prev?.updated_at === next?.updated_at) return prev;
          return next;
        });
      } catch (err: unknown) {
        if (isStaleSessionError(err)) {
          clearStaleAuth();
        }
        setSession(null);
        setUser(null);
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string) => {
    const supabase = getClient();
    if (!supabase?.auth) throw new Error('Supabase client not available');
    const redirectTo = authCallbackUrl();
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: redirectTo },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const supabase = getClient();
    if (!supabase?.auth) throw new Error('Supabase client not available');
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  /**
   * Hands off to Google and comes back at /auth/callback.
   *
   * The PKCE verifier is written by the browser client, which in this app may
   * be storing to localStorage rather than cookies (see supabase/client.ts).
   * The callback therefore completes the exchange in the browser, not on the
   * server, so it works in either storage mode.
   */
  const signInWithGoogle = async (returnTo?: string) => {
    const supabase = getClient();
    if (!supabase?.auth) throw new Error('Supabase client not available');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: authCallbackUrl(returnTo),
        // Without this Google silently reuses the last account whenever one
        // is already signed in, which makes "wrong account" unrecoverable
        // from inside the app.
        queryParams: { prompt: 'select_account' },
      },
    });
    if (error) throw error;
  };

  const signOut = async () => {
    const supabase = getClient();
    if (!supabase?.auth) return;
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  return (
    <AuthContext.Provider
      value={{ user, session, loading, signUp, signIn, signInWithGoogle, signOut }}
    >
      {children}
    </AuthContext.Provider>
  );
};
